import './OrgSwitcher.css'

import { Avatar } from 'arp/design-system/component/Avatar/Avatar'
import { FlyOut } from 'arp/design-system/component/FlyOut/FlyOut'
import { Input } from 'arp/design-system/component/Form/Input/Input'
import { Row } from 'arp/design-system/component/Layout/Row/Row'
import { Tooltip } from 'arp/design-system/component/Tooltip/Tooltip'
import { clsx } from 'clsx'
import type { ButtonHTMLAttributes, ReactElement, Ref } from 'react'
import { useEffect, useRef, useState } from 'react'
import { useGetSwitchableOrganizationsQuery } from '~/common/generated/apollo-operations'
import { useTypedTranslation } from '~/common/hooks/useTypedTranslation'
import {
  filterOrganizationsByName,
  switchOrganization,
} from '~/common/utils/organization-selection'
import { useDossSnackbar } from '../shared/DossSnackbar'

interface SwitchableOrganization {
  id: string
  name: string
  logoUrl: string | undefined
  isCurrent: boolean
}

interface OrgSwitcherTriggerProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  organizationName: string
  logoUrl: string | undefined
  ref?: Ref<HTMLButtonElement> | undefined
}

/** The nav row for the current organization. FlyOut injects the ref and click handling. */
const OrgSwitcherTrigger = ({
  organizationName,
  logoUrl,
  className,
  type = 'button',
  ...props
}: OrgSwitcherTriggerProps): ReactElement => (
  <button {...props} className={clsx('trigger', className)} type={type}>
    <Avatar name={organizationName} size="sm" src={logoUrl} type="entity" />
    <OrganizationName name={organizationName} />
  </button>
)

/** Long names are cut off with an ellipsis, so hovering shows the full name */
const OrganizationName = ({ name }: { name: string }): ReactElement => (
  <Tooltip onlyShowIfReferenceTruncated title={name}>
    <span className="name">{name}</span>
  </Tooltip>
)

interface OrgSwitcherProps {
  /** When true, the OrgSwitcher is in the floating/peeking LeftNav. Used to close the flyout
   * when the peeking nav closes. */
  isPeeking?: boolean
}

/**
 * The current organization in the left nav. For someone in several organizations it opens a
 * searchable list of them, and picking one switches the session to it.
 */
export const OrgSwitcher = ({ isPeeking = false }: OrgSwitcherProps): ReactElement | null => {
  const { t } = useTypedTranslation()
  const snackbar = useDossSnackbar()
  const { data } = useGetSwitchableOrganizationsQuery()
  const [isOpen, setIsOpen] = useState(false)
  const [query, setQuery] = useState('')

  // Close the flyout when the peeking LeftNav closes (isPeeking transitions from true to false)
  const wasPeekingRef = useRef(isPeeking)
  useEffect(() => {
    const wasPeeking = wasPeekingRef.current
    wasPeekingRef.current = isPeeking
    if (wasPeeking && !isPeeking && isOpen) {
      setIsOpen(false)
      setQuery('')
    }
  }, [isPeeking, isOpen])
  const organizations: SwitchableOrganization[] = (data?.switchableOrganizations ?? []).map(
    (organization) => ({
      id: organization.organizationId,
      name: organization.organizationName,
      logoUrl: organization.logoUrl ?? undefined,
      isCurrent: organization.isCurrent,
    }),
  )
  const current = organizations.find((organization) => organization.isCurrent)
  if (current === undefined) {
    return null
  }

  // Nothing to switch to: show where they are without a menu
  if (organizations.length === 1) {
    return (
      <div className="org-switcher arp-root">
        <div className="trigger">
          <Avatar name={current.name} size="sm" src={current.logoUrl} type="entity" />
          <OrganizationName name={current.name} />
        </div>
      </div>
    )
  }

  const handleOpenChange = (nextIsOpen: boolean): void => {
    setIsOpen(nextIsOpen)
    if (!nextIsOpen) {
      setQuery('')
    }
  }

  const handleSelect = async (organization: SwitchableOrganization): Promise<void> => {
    if (organization.isCurrent) {
      handleOpenChange(false)
      return
    }
    try {
      // Reloads the app into the other organization
      await switchOrganization(organization.id)
    } catch {
      snackbar.showError(t('left_nav.org_switcher.switch_failed'))
    }
  }

  const matches = filterOrganizationsByName(organizations, query)

  return (
    <div className="org-switcher arp-root">
      <FlyOut
        aria-label={t('left_nav.org_switcher.panel_label')}
        className="org-switcher-panel"
        isOpen={isOpen}
        onOpenChange={handleOpenChange}
        placement="top-start"
        role="dialog"
        trigger={{
          element: <OrgSwitcherTrigger logoUrl={current.logoUrl} organizationName={current.name} />,
        }}
      >
        <Row className="search">
          <Input
            aria-label={t('left_nav.org_switcher.search_label')}
            onChange={setQuery}
            onLiveChange={setQuery}
            placeholder={t('left_nav.org_switcher.search_placeholder')}
            size="full"
            value={query}
          />
        </Row>
        {matches.length === 0 ? (
          <p className="empty">{t('left_nav.org_switcher.no_results')}</p>
        ) : (
          <ul className="organizations">
            {matches.map((organization) => (
              <li key={organization.id}>
                <button
                  aria-current={organization.isCurrent ? true : undefined}
                  className="organization"
                  onClick={() => {
                    void handleSelect(organization)
                  }}
                  type="button"
                >
                  <Avatar
                    name={organization.name}
                    size="sm"
                    src={organization.logoUrl}
                    type="entity"
                  />
                  <OrganizationName name={organization.name} />
                </button>
              </li>
            ))}
          </ul>
        )}
      </FlyOut>
    </div>
  )
}
